import { useMemo } from 'react';

import Config from '@config';
import { EmployeeByRoleGridify } from '@gasbi/shared/common/features/employees';
import { initializeMainOrganizationApi } from '@gasbi/shared/common/features/mainOrganization';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';

import { injectAdminSlice } from '@/app/adminApi';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const {
  mainOrganizationApi,
  useGetMainOrganizationByIdQuery,
  useGetMainOrganizationsQuery,
  useGetMainOrganizationQuery,
  useCreateMainOrganizationMutation,
  useUpdateMainOrganizationByIdMutation,
  useAssignAdministratorMutation,
  useUnassignAdministratorMutation,
  useGetMainOrganizationRolesInfiniteQuery,
  useGetCachedRoleByIdSelector,
} = initializeMainOrganizationApi({
  baseUrl: Config.VITE_API_URL,
  baseQuery: axiosBaseQuery,
});

type RoleEmployeesQueryArg = FiltersGrid & {
  organizationId: string;
  roleId?: string;
  name?: string;
};

export const roleEmployeesApi = injectAdminSlice({
  baseUrl: `${Config.VITE_API_URL}/main-organizations`,
  tagTypes: ['EmployeesByRole'],
  endpoints: builder => ({
    getRoleEmployees: builder.infiniteQuery<
      EmployeeByRoleGridify,
      RoleEmployeesQueryArg,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/roles/employees',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['EmployeesByRole'],
    }),
    getRoleEmployeesList: builder.query<
      EmployeeByRoleGridify[],
      RoleEmployeesQueryArg
    >({
      query: params => ({
        url: '/roles/employees',
        params,
      }),
      providesTags: () => ['EmployeesByRole'],
    }),
  }),
});

export const useGetRoleEmployeesInfiniteQuery = normalizeInfiniteQueryData(
  roleEmployeesApi.useGetRoleEmployeesInfiniteQuery,
);

export const { useGetRoleEmployeesListQuery } = roleEmployeesApi;

export const useGetRolesForFilterQuery = (
  { organizationId, name }: { organizationId?: string; name?: string } = {},
  options?: { skip?: boolean },
) => {
  const result = useGetMainOrganizationRolesInfiniteQuery(
    organizationId ? { organizationId } : undefined,
    options,
  );

  const data = useMemo(() => {
    const roles = result.data ?? [];
    if (!name) return roles;
    const term = name.toLowerCase();
    return roles.filter(role => role.name.toLowerCase().includes(term));
  }, [result.data, name]);

  return { ...result, data };
};
